import { useCallback, useMemo } from 'react';
import {
  getObjectNameFromRecordTypeId,
  getRecordTypeIdentifier,
  getStepsFromAutomation,
} from '../helper';
import { classifyAutomation } from '../../pages/configuration-canvas/utils/automationClassificationHelper';
import { AutomationFiltersTypesMap } from './filtersTypes';
import { filterItemsBySearch } from '../../../lib/filterItemsBySearch';
import { AutomationActionType } from '../../../types/enums/AutomationActionType';
import { useAutomationFiltersContext } from './AutomationFiltersContext';

// Group similar Slack and Teams actions together
const ACTION_GROUPS: Partial<Record<AutomationActionType, AutomationActionType[]>> = {
  [AutomationActionType.SlackArchiveDealroom]: [
    AutomationActionType.SlackArchiveDealroom,
    AutomationActionType.TeamsArchiveDealroom,
  ],
  [AutomationActionType.TeamsArchiveDealroom]: [
    AutomationActionType.SlackArchiveDealroom,
    AutomationActionType.TeamsArchiveDealroom,
  ],
  [AutomationActionType.SlackRenameDealroom]: [
    AutomationActionType.SlackRenameDealroom,
    AutomationActionType.TeamsRenameDealroom,
  ],
  [AutomationActionType.TeamsRenameDealroom]: [
    AutomationActionType.SlackRenameDealroom,
    AutomationActionType.TeamsRenameDealroom,
  ],
  [AutomationActionType.SlackDealRoom]: [
    AutomationActionType.SlackDealRoom,
    AutomationActionType.TeamsDealRoom,
  ],
  [AutomationActionType.TeamsDealRoom]: [
    AutomationActionType.SlackDealRoom,
    AutomationActionType.TeamsDealRoom,
  ],
  [AutomationActionType.SlackAddMember]: [
    AutomationActionType.SlackAddMember,
    AutomationActionType.TeamsAddMember,
  ],
  [AutomationActionType.TeamsAddMember]: [
    AutomationActionType.SlackAddMember,
    AutomationActionType.TeamsAddMember,
  ],
};

// Tests if an automation is of a record type
const isAutomationOfRecordTypes = (automation: AutomationStructureNew, recordTypeIds: string[]) => {
  if (recordTypeIds.length === 0) {
    return false;
  }
  return recordTypeIds.some((recordTypeId) => {
    if (automation.funnelRecordType) {
      const automationRecordTypeId = getRecordTypeIdentifier(
        automation.funnelRecordType.name,
        automation.funnelRecordType.objectName,
      );
      return recordTypeId === automationRecordTypeId;
    }
    if (
      automation.automationDetails.recordTypes &&
      automation.automationDetails.recordTypes.length > 0
    ) {
      return automation.automationDetails.recordTypes.some((recordType) => {
        const automationRecordTypeId = getRecordTypeIdentifier(
          recordType.name,
          automation.objectName,
        );
        return recordTypeId === automationRecordTypeId;
      });
    }
  });
};

const isAutomationOfStep = (automation: AutomationStructureNew, items: string[]) => {
  const automationClassification = classifyAutomation(automation);
  if (automationClassification?.level === 'step') {
    return items.some((itemId) => {
      const stepsInAutomation = getStepsFromAutomation(automation.automationDetails);
      return stepsInAutomation.some((step) => step.value === itemId);
    });
  }
  return false;
};

export const useAutomationFilters = ({
  automations,
  search,
  setSearch,
}: {
  automations: AutomationStructureNew[];
  search: string;
  setSearch: (search: string) => void;
}) => {
  const { filterValues, clearAllFilterValues } = useAutomationFiltersContext();

  const clearFilters = useCallback(() => {
    clearAllFilterValues();
    setSearch('');
  }, [clearAllFilterValues, setSearch]);

  const objectsFilter = filterValues[AutomationFiltersTypesMap.object];
  const recordTypesFilter = filterValues[AutomationFiltersTypesMap.recordTypes];
  const stepsFilter = filterValues[AutomationFiltersTypesMap.steps];
  const activeFilter = filterValues[AutomationFiltersTypesMap.status];
  const actionsFilter = filterValues[AutomationFiltersTypesMap.actionTypes];
  const dedupAndMatchFilter = filterValues[AutomationFiltersTypesMap.operations];
  const deployRequestsFilter = filterValues[AutomationFiltersTypesMap.deploymentRequest];

  const filteredAutomations = useMemo(() => {
    let _filteredAutomations = [...automations];

    if (stepsFilter?.selectedValues && stepsFilter?.selectedValues.length) {
      const stepsData = stepsFilter.selectedValues;
      _filteredAutomations = _filteredAutomations.filter((automation) =>
        isAutomationOfStep(automation, stepsData),
      );
    } else if (recordTypesFilter?.selectedValues && recordTypesFilter?.selectedValues.length) {
      const recordTypeIds = recordTypesFilter.selectedValues;

      _filteredAutomations = _filteredAutomations.filter(
        (automation) =>
          (!automation.funnelRecordType &&
            recordTypeIds
              .map((rt) => getObjectNameFromRecordTypeId(rt))
              .includes(automation.objectName)) ||
          isAutomationOfRecordTypes(automation, recordTypeIds),
      );
    }

    if (objectsFilter?.selectedValues && objectsFilter?.selectedValues.length) {
      const objectNames = objectsFilter.selectedValues;
      _filteredAutomations = _filteredAutomations.filter((automation) =>
        objectNames.includes(automation.objectName),
      );
    }

    if (activeFilter?.selectedValues.length) {
      _filteredAutomations = _filteredAutomations.filter((automation) =>
        activeFilter.selectedValues.includes(automation.isActive ? 'true' : 'false'),
      );
    }

    if (actionsFilter?.selectedValues.length) {
      _filteredAutomations = _filteredAutomations.filter((automation) => {
        const actionsInUse = automation.automationDetails.actions.map(
          ({ actionType }) => actionType as AutomationActionType,
        );

        return actionsFilter.selectedValues.some((selectedAction) => {
          const group = ACTION_GROUPS[selectedAction as AutomationActionType];
          if (group) {
            return group.some((actionType: AutomationActionType) =>
              actionsInUse.includes(actionType),
            );
          }
          return actionsInUse.includes(selectedAction as AutomationActionType);
        });
      });
    }

    if (dedupAndMatchFilter?.selectedValues?.length) {
      _filteredAutomations = _filteredAutomations.filter((automation) => {
        const action = automation?.automationDetails?.actions?.[0];
        if (!action) return false;

        if (action.actionType === 'DEDUP' || action.actionType === 'MATCHING') {
          return dedupAndMatchFilter.selectedValues.includes(action.actionParams.dmType as string);
        }
      });
    }

    if (
      deployRequestsFilter?.selectedValues.length === 1 &&
      deployRequestsFilter.selectedValues[0] === 'true'
    ) {
      _filteredAutomations = _filteredAutomations.filter((automation) => {
        return automation.notifyAdmin;
      });
    }

    return filterItemsBySearch<AutomationStructureNew>(_filteredAutomations, search, 'name');
  }, [
    automations,
    stepsFilter?.selectedValues,
    recordTypesFilter?.selectedValues,
    objectsFilter?.selectedValues,
    actionsFilter?.selectedValues,
    dedupAndMatchFilter?.selectedValues,
    deployRequestsFilter?.selectedValues,
    activeFilter?.selectedValues,
    search,
  ]);

  return {
    filteredAutomations,
    clearFilters,
    filtersJsx: null,
  };
};
